'use client';

import {
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { ApolloError } from '@apollo/client';
import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import type { SequenceUploadJobProgressPayload } from '@frameio/graphql-client/generated/types';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { useCancelTransferBatchMutation } from '@frameio/transfers/gql/mutations/generated/CancelTransferBatch';
import {
  createOrUpdateTransferState,
  removeTransferState,
} from '@frameio/transfers/utils';
import { formatFileSizeInMB } from '@frameio/utils/formatters';
import { DocumentProgressStatus } from '../../../shared/components/DocumentExports/types';
import { isFailedDocumentProgressStatus } from '../../../shared/components/DocumentExports/utils';
import { useCreateTransferAssets } from '../../../shared/hooks/useCreateTransferAssets';
import { useDatadogLogger } from '../../../shared/hooks/useDatadogLogger';
import type { DeferredPlaceholder } from '../../../shared/hooks/useDeferredTransferAssets';
import { useInitializeDeferredTransfer } from '../../../shared/hooks/useDeferredTransferAssets';
import { ensureFileNamesHaveExtensions } from '../../../shared/utils/ensureFileNamesHaveExtensions';
import { useSequenceEventsSubscription } from '../../gql';
import type {
  ExportProgressItem,
  ExportProgressItems,
} from '../../types/exportProgressTypes';
import TEXT from './generated/TEXT/ExportProgressContext';

TEXT.DEFINE({
  /** Fallback message when an export failure event has no message body */
  unknownError: 'Unknown error',
});

/**
 * @description
 * This context provides a centralized mechanism for tracking the progress of sequence exports from Adobe Premiere Pro.
 * It handles the entire lifecycle of an export encoding and upload progress.
 *
 * - The process starts the encoding process and sends progress updates via the `useSequenceEventsSubscription`.
 * - When the export started with a deferred-size placeholder (created at export
 *   start via `addAssetsToTransferBatch(deferUpload: true)`), the item carries
 *   its `assetId`/`transferBatchId` from the start and the Finder card shows
 *   live encode progress.
 * - `SequenceEncodeProgressPayload` events set `status: Encoding` and advance `progress` through the encoding band (0..ENCODING_WEIGHT).
 * - On `SequenceEncodeCompletePayload`, the placeholder is finalized via `handleInitializeDeferredUpload` (`initializeAssetsUpload` sets the real size and mints upload URLs); items without a placeholder — or whose finalization fails — fall back to the legacy `handleCreateTransferJob`, which creates the asset post-encode. The item flips to `status: Uploading` at `progress = ENCODING_WEIGHT`.
 * - `SequenceUploadJobProgressPayload` events advance `progress` through the upload band (ENCODING_WEIGHT..1) and update bytes counters; `SequenceUploadJobItemProgressPayload` updates the per-asset transfer state.
 * - The job is marked `status: Completed` when the upload finishes, and the item is eventually removed from the active progress list.
 */
type ExportProgressContextType = {
  /**
   * This is used to track the progress of all active export jobs.
   */
  exportProgressItems: ExportProgressItems;
  /**
   * Adds a new export progress item to the map
   */
  addExportProgressItem: (
    batchEncoderId: string,
    exportItem: ExportProgressItem
  ) => void;
  updateExportProgressItem: (
    batchEncoderId: string,
    updates: Partial<ExportProgressItem>
  ) => void;
  /**
   * Attaches a resolved placeholder's ids to its item if the item is still
   * alive and has no real asset yet; otherwise tears the placeholder down.
   * Race-safe (unlike `updateExportProgressItem`) because placeholder creation
   * isn't awaited and can resolve after a cancel or the legacy fallback.
   */
  setDeferredPlaceholder: (
    batchEncoderId: string,
    placeholder:
      | Pick<ExportProgressItem, 'assetId' | 'transferBatchId' | 'projectId'>
      | undefined
  ) => void;
  /**
   * Calculates the progress of an export progress item based on the encoding and upload progress.
   */
  calculateExportProgressItemProgress: (id: string) => number;
  removeExportProgressItem: (id: string) => void;
};

/**
 * Constants for progress calculation.
 *
 * The total progress is a weighted average of the encoding and upload progress.
 * - Encoding accounts for `n`% of the total progress (`ENCODING_WEIGHT`).
 * - Uploading accounts for `n`% of the total progress (`UPLOAD_WEIGHT`).
 * This is calculated in `calculateExportProgressItemProgress`.
 */
const ENCODING_WEIGHT = 0.65;
const UPLOAD_WEIGHT = 0.35;

/**
 * The default context for the export progress items.
 */
export const ExportProgressContext = createContext<ExportProgressContextType>({
  addExportProgressItem: () => undefined,
  updateExportProgressItem: () => undefined,
  setDeferredPlaceholder: () => undefined,
  calculateExportProgressItemProgress: () => 0,
  exportProgressItems: {},
  removeExportProgressItem: () => undefined,
});

export const ExportProgressProvider = ({
  children,
}: NonNullable<PropsWithChildren>) => {
  const track = useTrackUnsafe();
  const [allExportProgressItems, setExportProgressItems] =
    useState<ExportProgressItems>({});

  /**
   * Synchronous mirror of the items map so subscription handlers and async
   * continuations don't act on a stale render snapshot.
   */
  const allItemsRef = useRef<ExportProgressItems>({});

  const applyExportProgressItems = useCallback(
    (updater: (prev: ExportProgressItems) => ExportProgressItems) => {
      allItemsRef.current = updater(allItemsRef.current);
      setExportProgressItems(allItemsRef.current);
    },
    []
  );

  /** Reads an item, applying the same completed-items filter as `exportProgressItems`. */
  const getActiveItem = useCallback((id: string) => {
    const item = allItemsRef.current[id];
    if (
      !item ||
      item.progress === 1 ||
      item.status === DocumentProgressStatus.Completed
    ) {
      return undefined;
    }
    return item;
  }, []);

  const exportProgressItems = useMemo(() => {
    return Object.entries(allExportProgressItems).reduce<ExportProgressItems>(
      (acc, [batchEncoderId, item]) => {
        if (
          item.progress === 1 ||
          item.status === DocumentProgressStatus.Completed
        )
          return acc;
        return { ...acc, [batchEncoderId]: item };
      },
      {}
    );
  }, [allExportProgressItems]);

  const removeExportProgressItem = useCallback(
    (id: string) => {
      applyExportProgressItems((prev) => {
        const items = { ...prev };
        delete items[id];
        return items;
      });
    },
    [applyExportProgressItems]
  );

  const createTransferAssets = useCreateTransferAssets();
  const initializeDeferredTransfer = useInitializeDeferredTransfer();
  const [cancelTransferJob] = useCancelTransferBatchMutation();
  const datadogLogger = useDatadogLogger();
  const refetchFinder = useFinderRefetch();

  const cancelTransferBatch = useCallback(
    (transferBatchId: string) =>
      cancelTransferJob({
        variables: {
          input: { transferBatchId, markAsHasViewed: false },
        },
      }).catch((error) => {
        datadogLogger.info('integrations-cancel-transfer-batch-failed', {
          uxpUploads: {
            source: 'PrUXP',
            message: error instanceof Error ? error.message : String(error),
          },
        });
      }),
    [cancelTransferJob, datadogLogger]
  );

  /**
   * Tears down a placeholder that lost the attach race — canceling its batch
   * deletes the incomplete asset server-side. Deduped by assetId.
   */
  const cleanedUpPlaceholderAssetIds = useRef(new Set<string>());
  const cleanupOrphanedPlaceholder = useCallback(
    (placeholder: DeferredPlaceholder) => {
      if (cleanedUpPlaceholderAssetIds.current.has(placeholder.assetId)) {
        return;
      }
      cleanedUpPlaceholderAssetIds.current.add(placeholder.assetId);

      removeTransferState(placeholder.assetId);
      cancelTransferBatch(placeholder.transferBatchId).then(() =>
        refetchFinder()
      );
      datadogLogger.info('integrations-deferred-placeholder-orphaned', {
        uxpUploads: { source: 'PrUXP' },
      });
    },
    [cancelTransferBatch, datadogLogger, refetchFinder]
  );

  const trackUploadStarted = useCallback(
    (
      jobId: string | undefined,
      totalFileSize: number | null | undefined,
      itemCount: number
    ) => {
      const trackData = {
        source: 'PrUXP',
        item_count: itemCount,
        transfer_job_id: jobId,
        total_file_size: formatFileSizeInMB(totalFileSize ?? 0),
      };

      track('upload-started', trackData);
      datadogLogger.info('integrations-upload-started', {
        uxpUploads: trackData,
      });
    },
    [datadogLogger, track]
  );

  const updateExportProgressItem = useCallback(
    (batchEncoderId: string, updates: Partial<ExportProgressItem>) => {
      const exportProgressItem = getActiveItem(batchEncoderId);
      if (!exportProgressItem) return;

      if (
        updates.status === DocumentProgressStatus.Canceled &&
        exportProgressItem.transferBatchId
      ) {
        // Drop the transfer state (CANCELED would render as "Upload failed")
        // and refetch once the batch cancel lands.
        cancelTransferBatch(exportProgressItem.transferBatchId).then(() =>
          refetchFinder()
        );
        if (exportProgressItem.assetId) {
          removeTransferState(exportProgressItem.assetId);
        }
        const trackData = {
          source: 'PrUXP',
          transfer_job_id: exportProgressItem.jobId,
        };

        track('upload-canceled', trackData);
        datadogLogger.info('integrations-upload-canceled', {
          uxpUploads: trackData,
        });
      }

      applyExportProgressItems((prev) => {
        if (prev[batchEncoderId]?.status === DocumentProgressStatus.Canceled) {
          return prev;
        }
        if (!prev[batchEncoderId]) return prev;
        return {
          ...prev,
          [batchEncoderId]: {
            ...prev[batchEncoderId],
            ...updates,
          },
        };
      });
    },
    [
      applyExportProgressItems,
      cancelTransferBatch,
      datadogLogger,
      getActiveItem,
      refetchFinder,
      track,
    ]
  );

  const handleCreateTransferJob = useCallback(
    async (id: string, filePath: string) => {
      const exportProgressItem = getActiveItem(id);
      if (!exportProgressItem || !exportProgressItem.folderId) {
        // eslint-disable-next-line no-console
        console.error(
          'No export found or missing folderId before creating transfer job'
        );
        return;
      }

      const filePaths = [filePath];
      const fileNames = [exportProgressItem.fileName];
      const { jobId, totalFileSize, assetIds, transferBatchId, projectId } =
        await createTransferAssets({
          jobId: id,
          filePaths,
          fileNames: ensureFileNamesHaveExtensions({ fileNames, filePaths }),
          folderAssetId: exportProgressItem.folderId,
        });

      if (!assetIds || !jobId) return;

      // The item may have been canceled, or had a placeholder attach, while
      // the create round trips were in flight.
      const latestItem = allItemsRef.current[id];
      if (latestItem?.status === DocumentProgressStatus.Canceled) {
        cancelTransferBatch(transferBatchId).then(() => refetchFinder());
        removeTransferState(assetIds[0]);
        return;
      }
      if (
        latestItem?.assetId &&
        latestItem.transferBatchId &&
        latestItem.assetId !== assetIds[0]
      ) {
        cleanupOrphanedPlaceholder({
          assetId: latestItem.assetId,
          transferBatchId: latestItem.transferBatchId,
          projectId: latestItem.projectId ?? '',
        });
      }

      updateExportProgressItem(id, {
        assetId: assetIds[0],
        transferBatchId,
        projectId,
        jobId,
      });

      trackUploadStarted(jobId, totalFileSize, assetIds.length);
    },
    [
      cancelTransferBatch,
      cleanupOrphanedPlaceholder,
      createTransferAssets,
      getActiveItem,
      refetchFinder,
      trackUploadStarted,
      updateExportProgressItem,
    ]
  );

  /**
   * Finalizes a deferred placeholder after encoding. Returns false when there's
   * no placeholder or finalization fails, so the caller can use the legacy path.
   */
  const handleInitializeDeferredUpload = useCallback(
    async (id: string, filePath: string): Promise<boolean> => {
      const exportProgressItem = getActiveItem(id);
      if (!exportProgressItem?.assetId || !exportProgressItem.transferBatchId) {
        return false;
      }

      const { assetId, transferBatchId, fileName } = exportProgressItem;

      try {
        const [fileNameWithExtension] = ensureFileNamesHaveExtensions({
          fileNames: [fileName],
          filePaths: [filePath],
        });

        const { jobId, totalFileSize } = await initializeDeferredTransfer({
          batchItemId: id,
          filePath,
          fileName: fileNameWithExtension,
          assetId,
          transferBatchId,
        });

        if (jobId) {
          updateExportProgressItem(id, { jobId });
        }

        trackUploadStarted(jobId, totalFileSize, 1);
        return true;
      } catch (error) {
        createOrUpdateTransferState(assetId, {
          status: TransferJobTaskStatus.FAILED,
          isEncoding: false,
          assetId,
        });
        cancelTransferBatch(transferBatchId);
        datadogLogger.info('integrations-deferred-upload-failed', {
          uxpUploads: {
            source: 'PrUXP',
            message: error instanceof Error ? error.message : String(error),
            // Surface the GraphQL error code (e.g. PERMISSION_DENIED).
            code:
              error instanceof ApolloError
                ? error.graphQLErrors[0]?.extensions?.code
                : undefined,
          },
        });
        return false;
      }
    },
    [
      cancelTransferBatch,
      datadogLogger,
      getActiveItem,
      initializeDeferredTransfer,
      trackUploadStarted,
      updateExportProgressItem,
    ]
  );

  const addExportProgressItem = useCallback(
    (batchEncoderId: string, exportItem: ExportProgressItem) => {
      applyExportProgressItems((prev) => ({
        ...prev,
        [batchEncoderId]: exportItem,
      }));
    },
    [applyExportProgressItems]
  );

  const setDeferredPlaceholder = useCallback(
    (
      batchEncoderId: string,
      placeholder:
        | Pick<ExportProgressItem, 'assetId' | 'transferBatchId' | 'projectId'>
        | undefined
    ) => {
      const item = getActiveItem(batchEncoderId);
      // Can't attach (item gone, dead, or already has a real asset): tear the
      // placeholder down so its batch/asset don't leak.
      const canAttach =
        item && !item.assetId && !isFailedDocumentProgressStatus(item.status);
      if (!canAttach) {
        if (placeholder?.assetId && placeholder.transferBatchId) {
          cleanupOrphanedPlaceholder({
            assetId: placeholder.assetId,
            transferBatchId: placeholder.transferBatchId,
            projectId: placeholder.projectId ?? '',
          });
        }
        return;
      }
      applyExportProgressItems((prev) => ({
        ...prev,
        [batchEncoderId]: {
          ...prev[batchEncoderId],
          assetId: placeholder?.assetId,
          transferBatchId: placeholder?.transferBatchId,
          projectId: placeholder?.projectId,
        },
      }));
    },
    [applyExportProgressItems, cleanupOrphanedPlaceholder, getActiveItem]
  );

  const calculateExportProgressItemProgress = useCallback(
    (id: string) =>
      Math.min((exportProgressItems[id]?.progress ?? 0) * 100, 100),
    [exportProgressItems]
  );

  const handleJobProgress = useCallback(
    (jobProgress: SequenceUploadJobProgressPayload) => {
      const exportProgressItem = getActiveItem(jobProgress.id);
      if (!exportProgressItem) return;

      if (exportProgressItem.status === DocumentProgressStatus.Canceled) {
        return;
      }

      const uploadPercent = jobProgress.percentage ?? jobProgress.progress ?? 0;
      updateExportProgressItem(jobProgress.id, {
        status: getUploadJobStatus(jobProgress),
        progress: ENCODING_WEIGHT + uploadPercent * UPLOAD_WEIGHT,
        bytesSent: jobProgress.bytesSent || undefined,
        bytesTotal: jobProgress.bytesTotal || undefined,
      });

      if (jobProgress.state === 'failed') {
        const trackData = {
          source: 'PrUXP',
          transfer_job_id:
            exportProgressItem.jobId ?? jobProgress.jobId ?? jobProgress.id,
        };
        track('upload-failed', trackData);
        datadogLogger.info('integrations-upload-failed', {
          uxpUploads: trackData,
        });
      }

      if (isJobComplete(jobProgress)) {
        if (exportProgressItem.onExportCompleteCallback) {
          exportProgressItem.onExportCompleteCallback(exportProgressItem);
        }
        const trackData = {
          source: 'PrUXP',
          transfer_job_id:
            exportProgressItem.jobId ?? jobProgress.jobId ?? jobProgress.id,
          completed_item_count: jobProgress.completedItems,
          failed_item_count: jobProgress.failedItems,
          item_count: jobProgress.totalItems,
          total_file_size: jobProgress.bytesTotal
            ? formatFileSizeInMB(jobProgress.bytesTotal)
            : 0,
        };
        track('upload-completed', trackData);
        datadogLogger.info('integrations-upload-completed', {
          uxpUploads: trackData,
        });
      }
    },
    [updateExportProgressItem, track, getActiveItem, datadogLogger]
  );

  useSequenceEventsSubscription({
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.sequenceEvents;
      if (!event) return;
      switch (event?.__typename) {
        case 'SequenceEncodeProgressPayload': {
          updateExportProgressItem(event.id, {
            status: DocumentProgressStatus.Encoding,
            progress: (event.progress ?? 0) * ENCODING_WEIGHT,
          });
          const placeholderAssetId = getActiveItem(event.id)?.assetId;
          if (placeholderAssetId) {
            createOrUpdateTransferState(placeholderAssetId, {
              status: TransferJobTaskStatus.UPLOADING,
              isEncoding: true,
              assetId: placeholderAssetId,
              progress: {
                // Round so sub-percent ticks are skipped by the no-op guard.
                percentage: Math.round(
                  Math.max(0, Math.min(100, (event.progress ?? 0) * 100))
                ),
              },
            });
          }
          break;
        }
        case 'SequenceEncodeProgressErrorPayload': {
          const failedItem = getActiveItem(event.id);
          updateExportProgressItem(event.id, {
            status: DocumentProgressStatus.Failed,
            failedMessage: event?.message || TEXT.unknownError,
          });
          if (failedItem?.assetId) {
            createOrUpdateTransferState(failedItem.assetId, {
              status: TransferJobTaskStatus.FAILED,
              isEncoding: false,
              assetId: failedItem.assetId,
            });
          }
          // Cancel the batch to delete the incomplete asset server-side (the
          // Canceled-transition cleanup doesn't run for failures).
          if (failedItem?.transferBatchId) {
            cancelTransferBatch(failedItem.transferBatchId);
          }
          datadogLogger.info('integrations-encode-failed', {
            uxpUploads: {
              source: 'PrUXP',
              message: event?.message || TEXT.unknownError,
            },
          });
          break;
        }
        case 'SequenceUploadJobProgressPayload':
          handleJobProgress(event);
          break;
        case 'SequenceEncodeCompletePayload':
          updateExportProgressItem(event.id, {
            status: DocumentProgressStatus.Uploading,
            progress: ENCODING_WEIGHT,
          });
          handleInitializeDeferredUpload(event.id, event.filePath)
            .then((initialized) => {
              if (!initialized) {
                return handleCreateTransferJob(event.id, event.filePath);
              }
              return undefined;
            })
            .catch((error) => {
              datadogLogger.info('integrations-upload-fallback-failed', {
                uxpUploads: {
                  source: 'PrUXP',
                  message:
                    error instanceof Error ? error.message : String(error),
                },
              });
            });
          datadogLogger.info('integrations-encode-completed', {
            uxpUploads: { source: 'PrUXP' },
          });
          break;
        case 'SequenceEncodeCanceledPayload': {
          // The Canceled transition handles cleanup (cancel batch, drop state,
          // refetch); an in-flight placeholder is torn down by setDeferredPlaceholder.
          updateExportProgressItem(event.id, {
            status: DocumentProgressStatus.Canceled,
          });
          datadogLogger.info('integrations-encode-canceled', {
            uxpUploads: { source: 'PrUXP' },
          });
          break;
        }
        case 'SequenceUploadJobItemProgressPayload': {
          if (event.state === 'canceled') {
            // CANCELED would render as "Upload failed"; drop the entry instead.
            removeTransferState(event.assetId);
            break;
          }
          const { bytesSent, bytesTotal, remainingTime } = event;
          const bytesPerSecond =
            bytesSent != null && bytesTotal != null && remainingTime
              ? Math.max(0, (bytesTotal - bytesSent) / remainingTime)
              : undefined;

          createOrUpdateTransferState(event.assetId, {
            status: getTransferJobTaskStatus(event.state ?? undefined),
            isEncoding: false,
            assetId: event.assetId,
            progress: {
              bytesSent: event.bytesSent,
              bytesTotal: event.bytesTotal,
              bytesPerSecond,
              percentage: Math.max(
                0,
                Math.min(100, (event.percentage ?? 0) * 100)
              ),
              remainingTime: event.remainingTime,
            },
            filePath: event.filePath,
          });
          break;
        }
        default:
          break;
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  const value = useMemo(
    () => ({
      addExportProgressItem,
      updateExportProgressItem,
      setDeferredPlaceholder,
      calculateExportProgressItemProgress,
      exportProgressItems,
      removeExportProgressItem,
    }),
    [
      addExportProgressItem,
      updateExportProgressItem,
      setDeferredPlaceholder,
      calculateExportProgressItemProgress,
      exportProgressItems,
      removeExportProgressItem,
    ]
  );

  return (
    <ExportProgressContext.Provider value={value}>
      {children}
    </ExportProgressContext.Provider>
  );
};

// Export hooks for accessing the context
export const useExportProgressItems = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.exportProgressItems
  );

export const useAddExportProgressItem = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.addExportProgressItem
  );

export const useUpdateExportProgressItem = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.updateExportProgressItem
  );

export const useSetDeferredPlaceholder = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.setDeferredPlaceholder
  );

export const useRemoveExportProgressItem = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.removeExportProgressItem
  );

export const useCalculateExportProgressItemProgress = () =>
  useContextSelector(
    ExportProgressContext,
    (context) => context.calculateExportProgressItemProgress
  );

export const useInProgressExportCount = () =>
  useContextSelector(
    ExportProgressContext,
    (context) =>
      Object.values(context.exportProgressItems).filter(
        (item) => !isFailedDocumentProgressStatus(item.status)
      ).length
  );

const getTransferJobTaskStatus = (state?: string) => {
  if (state === 'canceled') {
    return TransferJobTaskStatus.CANCELED;
  }
  if (state === 'completed') {
    return TransferJobTaskStatus.TRANSFER_PARTS_COMPLETE;
  }
  if (state === 'failed') {
    return TransferJobTaskStatus.FAILED;
  }
  return TransferJobTaskStatus.UPLOADING;
};

/**
 * Checks if a job is complete by testing multiple completion conditions.
 * This function is needed to ensure backwards compatibility with different
 * versions of the job progress payload that may use different field names
 * or completion indicators (percentage vs progress).
 */
const isJobComplete = (jobProgress: SequenceUploadJobProgressPayload) => {
  return (
    jobProgress.state === 'completed' ||
    jobProgress.percentage === 1 ||
    jobProgress.progress === 1
  );
};

/**
 * Maps a `SequenceUploadJobProgressPayload` onto the canonical document
 * `DocumentProgressStatus`. `canceled`/`failed` win over completion; otherwise an item that
 * meets `isJobComplete` is `Completed`, and everything else is still
 * `Uploading`.
 */
const getUploadJobStatus = (
  jobProgress: SequenceUploadJobProgressPayload
): DocumentProgressStatus => {
  if (jobProgress.state === 'canceled') return DocumentProgressStatus.Canceled;
  if (jobProgress.state === 'failed') return DocumentProgressStatus.Failed;
  if (isJobComplete(jobProgress)) return DocumentProgressStatus.Completed;
  return DocumentProgressStatus.Uploading;
};
